import { Component, DestroyRef, inject, OnInit, Output, EventEmitter, TemplateRef, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { NZ_DRAWER_DATA, NzDrawerRef } from 'ng-zorro-antd/drawer';
import { GlobalValuesService } from '@app/core';
import { ScreenService, OpportunityService, CompanyService, ProductsService } from '@app/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize, combineLatest, map } from 'rxjs';
import { isProductInCompanyScope, Partner, Product, SaleOrder, SaleOrderLineOptions, ToastConfig } from '@app/shared';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { NzNotificationRef } from 'ng-zorro-antd/notification';
import { ModifyPartnerDrawerComponent } from 'src/app/features/maestros/drawers/modify-partner-drawer/modify-partner-drawer.component';
import { resolveExplicitTaxIds, resolveOfferedTaxes } from '../tax-catalog-lookup.util';
import { resolveLineWriteErrorMessage } from '../../components/budget-content/budget-content-line-error.util';

@Component({
  selector: 'app-create-sale-order-line-drawer',
  standalone: false,
  templateUrl: './create-sale-order-line-drawer.component.html',
  styleUrl: './create-sale-order-line-drawer.component.less',
})
export class CreateSaleOrderLineDrawerComponent implements OnInit {
  nzData: {
    saleOrderId?: string | number;
    saleOrder?: SaleOrder;
    lineData?: any;
    saleOrderLines?: any[];
  } = inject(NZ_DRAWER_DATA);

  @Output() lineCreated = new EventEmitter<any>();
  @ViewChild('notificationTmpl') notificationTmpl?: TemplateRef<any>;
  ModifyPartnerDrawerComponent = ModifyPartnerDrawerComponent;

  saleOrderLineForm: FormGroup;

  // Loading states
  saving: boolean = false;

  // Data lists
  productList: Product[] = [];
  supplierList: any[] = [];
  unitList: any[] = [];
  filteredUnitList: any[] = [];
  currencies: any[] = [];
  companyTaxes: any[] = [];
  filteredTaxes: any[] = [];
  // CEC-687: catálogo de impuestos de TODAS las compañías, para poder enseñar el que ya tiene la línea.
  private taxCatalog: any[] = [];
  locationList: any[] = [];
  notRef?: NzNotificationRef;
  toastConfig: ToastConfig = {
    icon: 'check_circle',
    iconColor: '#6CDA75',
    content: '',
    showUndo: false,
  };

  private destroyRef = inject(DestroyRef);
  private opportunityService = inject(OpportunityService);
  private companyService = inject(CompanyService);
  private lineOptions?: SaleOrderLineOptions;

  constructor(
    private drawerRef: NzDrawerRef<string>,
    private globalValuesService: GlobalValuesService,
    public screenService: ScreenService,
    private fb: FormBuilder,
    private notificationService: NzNotificationService,
    private productsService: ProductsService
  ) {
    this.saleOrderLineForm = this.fb.group({
      name: ['', Validators.required],
      productId: [null, this.productRequiredValidator],
      supplierId: [0],
      quantity: [0, [Validators.required, Validators.min(0)]],
      unitOfMeasureId: [0],
      price: [0, [Validators.required, Validators.min(0)]],
      discount: [0, [Validators.min(0), Validators.max(100)]],
      currencyId: [this.getSaleOrderCurrencyId(), Validators.required],
      amount: [{ value: 0, disabled: true }],
      taxIds: [null], // Single selection
      sourceId: [null],
      destinationId: [null]
    });

    // Suscribirse a cambios para calcular el importe
    this.saleOrderLineForm.get('quantity')?.valueChanges.subscribe(() => this.calculateAmount());
    this.saleOrderLineForm.get('price')?.valueChanges.subscribe(() => this.calculateAmount());
    this.saleOrderLineForm.get('discount')?.valueChanges.subscribe(() => this.calculateAmount());
    this.saleOrderLineForm.get('currencyId')?.valueChanges.subscribe(() => this.calculateAmount());

    // Suscribirse a cambios de producto para autocompletar campos
    this.saleOrderLineForm.get('productId')?.valueChanges.subscribe((productId) => {
      this.onProductChange(productId);
      this.updateFilteredTaxes();
    });
  }

  ngOnInit(): void {
    // Cargar listas de referencia
    this.loadReferenceData();

    // Si hay datos de línea (modo edición), inicializar formulario
    if (this.nzData.lineData) {
      this.initFormWithData(this.nzData.lineData);
    }

    this.calculateAmount();
  }

  get isEditMode(): boolean {
    return !!this.nzData.lineData?.id;
  }

  private getSaleOrderId(): number | null {
    const saleOrderId = this.nzData.saleOrder?.id || this.nzData.saleOrderId;
    return saleOrderId ? Number(saleOrderId) : null;
  }

  // Validador personalizado para requerir que el producto no sea 0
  productRequiredValidator = (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (value === null || value === undefined || value === 0) {
      return { required: true };
    }
    return null;
  };

  initFormWithData(lineData: any): void {
    // Preparar taxIds: convertir de array a valor simple si es necesario
    let taxId = null;
    if (lineData.taxIds && Array.isArray(lineData.taxIds) && lineData.taxIds.length > 0) {
      taxId = lineData.taxIds[0];
    } else if (lineData.taxes && Array.isArray(lineData.taxes) && lineData.taxes.length > 0) {
      // Si taxes es un array de objetos o números
      taxId = typeof lineData.taxes[0] === 'object' ? (lineData.taxes[0].id || lineData.taxes[0].taxId) : lineData.taxes[0];
    }

    const productId = lineData.productId || lineData.product?.id || lineData.product || 0;

    this.saleOrderLineForm.patchValue({
      name: lineData.name || '',
      productId: productId,
      supplierId: lineData.supplierId || lineData.supplier?.id || lineData.supplier || 0,
      quantity: lineData.quantity || 0,
      unitOfMeasureId: lineData.unitOfMeasureId || lineData.unitMeasure?.id || lineData.unit || 0,
      price: lineData.priceUnit || lineData.price || 0,
      discount: lineData.discount || 0,
      currencyId: lineData.currencyId || lineData.currency?.id || lineData.currency || this.getSaleOrderCurrencyId(),
      taxIds: taxId,
      sourceId: lineData.sourceId || null,
      destinationId: lineData.destinationId || null
    });

    // CEC-689: el impuesto de la línea tiene que estar entre las opciones desde el primer pintado. Sin esto
    // el selector recibe un valor que su lista aún no trae y queda vacío hasta que llegan las line-options;
    // a veces no se repinta y se queda vacío (PRE: 6 de 70 aperturas). Igual que el drawer de presupuesto.
    this.updateFilteredTaxes();

    this.updateUnitOptionsForProduct(productId);

    // Deshabilitar unidad de medida solo cuando el producto tenga una única unidad posible.
    if (productId && productId !== 0) {
      this.updateUnitControlState(productId);
    }
  }

  loadReferenceData(): void {
    const saleOrderId = this.getSaleOrderId();
    if (saleOrderId) {
      this.loadSaleOrderLineOptions(saleOrderId);
    }

    // Cargar proveedores
    this.globalValuesService.isPartnerReady$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((isReady) => {
        if (isReady) {
          const partnerList = this.globalValuesService.partnerlist || [];
          // Suppliers son partners con partnerTypeId === 6 o 7 (providers)
          this.supplierList = partnerList.filter((p: any) => p.partnerTypeId === 6 || p.partnerTypeId === 7) || [];
        }
      });

    // También obtener el valor actual si ya está listo
    if (this.globalValuesService.isPartnerReady$.value) {
      const partnerList = this.globalValuesService.partnerlist || [];
      this.supplierList = partnerList.filter((p: any) => p.partnerTypeId === 6 || p.partnerTypeId === 7) || [];
    }

    // Cargar productos filtrados por la compañía actual y tipo venta
    combineLatest([
      this.globalValuesService.products$,
      this.companyService.selectedCompanyId$
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([products, companyId]) => {
        if (this.lineOptions) {
          return;
        }

        if (companyId) {
          // Filtrar productos por la compañía actualmente seleccionada y solo venta.
          // Un producto global (companyId null) es válido para cualquier compañía, igual que
          // en el servidor (ProductScopeRules) — este filtro es solo el fallback de pre-carga.
          this.productList = (products || []).filter((product: any) =>
            isProductInCompanyScope(product, companyId) &&
            product.saleOk === true
          );
          this.updateUnitOptionsForProduct(this.saleOrderLineForm.get('productId')?.value);
          this.updateUnitControlState(this.saleOrderLineForm.get('productId')?.value);
        } else {
          this.productList = [];
          this.updateUnitOptionsForProduct(null);
          this.saleOrderLineForm.get('unitOfMeasureId')?.enable();
        }
      });

    // Cargar unidades de medida
    this.globalValuesService.unitOfMeasures$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((units) => {
        if (this.lineOptions) {
          return;
        }
        this.unitList = units || [];
        this.updateUnitOptionsForProduct(this.saleOrderLineForm.get('productId')?.value);
      });

    // También obtener el valor actual si ya está listo
    if (this.globalValuesService.isUnitOfMeasureReady$.value) {
      if (!this.lineOptions) {
        this.unitList = this.globalValuesService.unitOfMeasureslist || [];
        this.updateUnitOptionsForProduct(this.saleOrderLineForm.get('productId')?.value);
      }
    }

    // Cargar monedas
    this.globalValuesService.currencies$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((currencies) => {
        this.currencies = currencies || [];
      });

    // También obtener el valor actual si ya está listo
    if (this.globalValuesService.currencies$.value.length > 0) {
      this.currencies = this.globalValuesService.currencies$.value || [];
    }

    // Cargar localizaciones
    this.globalValuesService.places$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((locations: any[]) => {
        this.locationList = locations || [];
      });

    // Cargar taxes de la compañía
    combineLatest([
      this.globalValuesService.companyTaxes$,
      this.companyService.selectedCompanyId$
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([taxes, companyId]) => {
        this.taxCatalog = taxes || [];
        if (this.lineOptions) {
          this.updateFilteredTaxes();
          return;
        }
        // CEC-689 · opción C: la lista provisional (hasta que llegan las line-options) es la de la compañía de la
        // cotización, no la del conmutador.
        const quoteCompanyId = this.nzData.saleOrder?.companyId ?? companyId;
        if (quoteCompanyId) {
          this.companyTaxes = (taxes || []).filter((tax: any) => tax.companyId === quoteCompanyId);
          this.updateFilteredTaxes();
        } else {
          this.companyTaxes = [];
          this.filteredTaxes = [];
        }
      });
  }

  private loadSaleOrderLineOptions(saleOrderId: number): void {
    this.opportunityService
      .getSaleOrderLineOptions(saleOrderId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: any) => {
          const options: SaleOrderLineOptions = response?.data || response;
          this.lineOptions = options;
          this.productList = (options.products || []) as Product[];
          this.unitList = options.unitsOfMeasure || [];
          this.companyTaxes = options.taxes || [];
          this.updateUnitOptionsForProduct(this.saleOrderLineForm.get('productId')?.value);
          this.updateFilteredTaxes();
        },
        error: (error) => {
          console.error('Error loading sale order line options', error);
          this.showToast(
            error?.error?.message || 'No se pudieron cargar las opciones de línea del pedido.',
            'error'
          );
        },
      });
  }

  onSupplierCreated(partner: Partner): void {
    if (!partner?.id) {
      return;
    }

    if (!this.supplierList.some((supplier) => Number(supplier.id) === Number(partner.id))) {
      this.supplierList = [...this.supplierList, partner];
    }

    this.saleOrderLineForm.patchValue({ supplierId: partner.id });
  }

  onProductChange(productId: number | null): void {
    if (!productId || productId === 0) {
      this.filteredUnitList = this.unitList;
      this.saleOrderLineForm.get('unitOfMeasureId')?.enable();
      return;
    }

    const selectedProduct = this.productList.find(p => p.id === productId);
    if (!selectedProduct) {
      return;
    }

    // Autocompletar campos con los datos del producto
    const updates: any = {};
    const productDescription = this.getProductLineDescription(selectedProduct);
    if (productDescription && !this.isEditMode) {
      updates.name = productDescription;
    }

    // Precio unitario
    if (selectedProduct.unitPrice !== undefined && selectedProduct.unitPrice !== null) {
      updates.price = selectedProduct.unitPrice;
    }

    this.updateUnitOptionsForProduct(productId);
    const productUnitIds = this.getProductUnitIds(selectedProduct);
    const defaultUnitId = this.getDefaultProductUnitId(selectedProduct);
    if (defaultUnitId) {
      updates.unitOfMeasureId = defaultUnitId;
    } else if (
      productUnitIds.length > 1 &&
      !productUnitIds.includes(Number(this.saleOrderLineForm.get('unitOfMeasureId')?.value))
    ) {
      updates.unitOfMeasureId = 0;
    }
    this.updateUnitControlState(productId);

    if (!this.saleOrderLineForm.get('currencyId')?.value) {
      updates.currencyId = this.getSaleOrderCurrencyId();
    }

    // Aplicar los cambios al formulario
    if (Object.keys(updates).length > 0) {
      this.saleOrderLineForm.patchValue(updates, { emitEvent: false });
      // Recalcular el importe después de actualizar el precio
      if (updates.price !== undefined) {
        this.calculateAmount();
      }
    }

    // Por defecto la linea nueva queda sin impuesto; el usuario puede seleccionar uno.
    this.updateFilteredTaxes();
    this.saleOrderLineForm.patchValue({ taxIds: null }, { emitEvent: false });
  }

  private getProductUnitIds(product: any): number[] {
    const ids = [
      ...(Array.isArray(product?.suggestedUnitOfMeasureIds) ? product.suggestedUnitOfMeasureIds : []),
      ...(Array.isArray(product?.unitOfMeasureIds) ? product.unitOfMeasureIds : []),
      ...(Array.isArray(product?.unitOfMeasures)
        ? product.unitOfMeasures.map((unit: any) => unit?.id)
        : []),
      product?.unitOfMeasureId,
      product?.unitOfMeasure?.id,
    ];

    return [...new Set(ids.map((id) => Number(id)).filter((id) => id > 0))];
  }

  private getDefaultProductUnitId(product: any): number {
    const unitIds = this.getProductUnitIds(product);
    const defaultUnitId = Number(product?.defaultUnitOfMeasureId || product?.unitOfMeasureId || product?.unitOfMeasure?.id || 0);
    if (defaultUnitId && unitIds.includes(defaultUnitId)) {
      return defaultUnitId;
    }

    return unitIds.length === 1 ? unitIds[0] : 0;
  }

  private getProductLineDescription(product: Product | any): string {
    return product?.description || product?.name || '';
  }

  getSelectedProductUnitCategoryId(): number | null {
    const productId = Number(this.saleOrderLineForm.get('productId')?.value || 0);
    const product = this.productList.find((p) => Number(p.id) === productId);
    const defaultUnitId = this.getDefaultProductUnitId(product) || this.getProductUnitIds(product)[0];
    const defaultUnit = this.unitList.find((unit) => Number(unit.id) === defaultUnitId);

    return Number(defaultUnit?.unitOfMeasureCategoryId || 0) || null;
  }

  private getUnitsForProduct(_productId: number | null): any[] {
    return this.unitList;
  }

  private updateUnitOptionsForProduct(productId: number | null): void {
    this.filteredUnitList = this.getUnitsForProduct(productId);
    this.mergeSelectedUnit();
  }

  private updateUnitControlState(productId: number | null): void {
    const unitControl = this.saleOrderLineForm.get('unitOfMeasureId');
    unitControl?.enable();
  }

  onUnitOfMeasureCreated(unit: any): void {
    const productId = Number(this.saleOrderLineForm.get('productId')?.value || 0);
    this.associateUnitToProduct(productId, unit);
  }

  private associateUnitToProduct(productId: number, unit: any): void {
    const unitId = Number(unit?.id || 0);
    const product = this.productList.find((p) => Number(p.id) === productId);
    if (!product || !unitId) {
      return;
    }

    const unitIds = this.getProductUnitIds(product);
    if (unitIds.includes(unitId)) {
      this.updateUnitOptionsForProduct(productId);
      return;
    }

    const defaultUnit = this.unitList.find((u) => Number(u.id) === this.getDefaultProductUnitId(product));
    const defaultCategoryId = Number(defaultUnit?.unitOfMeasureCategoryId || 0);
    const newCategoryId = Number(unit?.unitOfMeasureCategoryId || 0);
    if (defaultCategoryId && newCategoryId && defaultCategoryId !== newCategoryId) {
      this.showToast(
        'La unidad creada pertenece a otra categoria y no se puede asociar al producto.',
        'warning'
      );
      this.saleOrderLineForm.patchValue({ unitOfMeasureId: this.getDefaultProductUnitId(product) || 0 }, { emitEvent: false });
      this.updateUnitOptionsForProduct(productId);
      return;
    }

    const updatedUnitIds = [...unitIds, unitId];
    const payload = this.buildProductUnitUpdatePayload(product, updatedUnitIds, unitId);

    this.productsService.updateProducts(payload).subscribe({
      next: () => {
        product.unitOfMeasureIds = updatedUnitIds;
        product.unitOfMeasures = [
          ...(product.unitOfMeasures || []),
          unit,
        ].filter((item, index, list) => list.findIndex((u) => Number(u.id) === Number(item.id)) === index);
        this.globalValuesService.products$.next([...this.productList]);
        this.updateUnitOptionsForProduct(productId);
      },
      error: (error) => {
        console.error('Error associating unit of measure to product', error);
        this.showToast(
          error?.error?.message || 'La unidad fue creada, pero no se pudo asociar al producto.',
          'error'
        );
        this.saleOrderLineForm.patchValue({ unitOfMeasureId: this.getDefaultProductUnitId(product) || 0 }, { emitEvent: false });
        this.updateUnitOptionsForProduct(productId);
      },
    });
  }

  private buildProductUnitUpdatePayload(product: Product, unitOfMeasureIds: number[], fallbackUnitId: number): Product {
    return {
      id: product.id,
      name: product.name,
      defaultCode: product.defaultCode,
      description: product.description,
      productType: product.productType || 'Service',
      unitPrice: product.unitPrice,
      costPrice: product.costPrice,
      saleOk: product.saleOk ?? true,
      purchaseOk: product.purchaseOk ?? true,
      isActive: product.isActive ?? true,
      companyId: product.companyId,
      unitOfMeasureId: this.getDefaultProductUnitId(product) || fallbackUnitId,
      unitOfMeasureIds,
      taxIds: product.taxes?.map((tax) => tax.id),
      supplierTaxIds: product.supplierTaxes?.map((tax) => tax.id),
    } as Product;
  }

  private showToast(content: string, type: 'success' | 'warning' | 'error' = 'success', msg?: string): void {
    const configByType = {
      success: { icon: 'check_circle', iconColor: '#6CDA75' },
      warning: { icon: 'warning', iconColor: '#E5A100' },
      error: { icon: 'dangerous', iconColor: '#CB2A2F' },
    };

    this.toastConfig = {
      ...configByType[type],
      content,
      msg,
      showUndo: false,
    };

    if (!this.notificationTmpl) {
      return;
    }

    this.notRef = this.notificationService.create(
      'blank',
      '',
      this.notificationTmpl,
      {
        nzPlacement: 'bottomRight',
        nzClass: 'custom-notification',
        // CEC-687: un fallo no se desvanece solo; se queda hasta cerrarlo con la X. Los demás, como siempre.
        ...(type === 'error' ? { nzDuration: 0 } : {}),
      }
    );
  }

  updateFilteredTaxes(): void {
    const productId = this.saleOrderLineForm.get('productId')?.value;

    // Si no hay producto seleccionado, no mostrar taxes
    if (!productId || productId === 0) {
      this.filteredTaxes = [];
      // Limpiar el tax seleccionado si no hay producto
      this.saleOrderLineForm.patchValue({ taxIds: null }, { emitEvent: false });
      return;
    }

    // CEC-687: nunca se descarta el impuesto que ya tiene la línea, aunque su producto esté archivado
    // o el impuesto sea de otra compañía. Vaciarlo hacía que al guardar se enviara taxIds: [] y el
    // servidor lo borrara. Ahora se enseña y se envía tal cual: si no vale, el servidor lo rechaza y se avisa.
    this.filteredTaxes = resolveOfferedTaxes(this.companyTaxes, this.saleOrderLineForm.get('taxIds')?.value, this.taxCatalog);
  }

  private mergeSelectedUnit(): void {
    const currentUnitId = Number(this.saleOrderLineForm.get('unitOfMeasureId')?.value || 0);
    if (!currentUnitId || this.unitList.some((unit) => Number(unit.id) === currentUnitId)) {
      return;
    }

    const unit = this.nzData.lineData?.unitOfMeasure || this.nzData.lineData?.unitMeasure;
    if (unit?.id) {
      this.unitList = [...this.unitList, unit];
      this.filteredUnitList = [...this.filteredUnitList, unit];
    }
  }

  calculateAmount(): void {
    const quantity = this.saleOrderLineForm.get('quantity')?.value || 0;
    const price = this.saleOrderLineForm.get('price')?.value || 0;
    const discount = this.saleOrderLineForm.get('discount')?.value || 0;

    // Siempre es unitario (isUnitPrice = true), usar cantidad real
    const subtotal = quantity * price;
    const discountAmount = subtotal * (discount / 100);
    const amount = subtotal - discountAmount;

    this.saleOrderLineForm.patchValue({ amount }, { emitEvent: false });
  }

  getCurrencyCode(): string {
    const currencyId = this.saleOrderLineForm.get('currencyId')?.value || this.getSaleOrderCurrencyId();
    const currency = this.currencies.find(c => c.id === currencyId);
    return currency?.isoCode || currency?.code || 'USD';
  }

  private getSaleOrderCurrencyId(): number {
    return this.nzData.saleOrder?.currencyId || 1;
  }

  close(): void {
    this.drawerRef.close();
  }

  discard(): void {
    this.close();
  }

  saveAndNew(): void {
    this.saveLine(true);
  }

  saveAndClose(): void {
    this.saveLine(false);
  }

  private saveLine(createNew: boolean): void {
    if (this.saleOrderLineForm.invalid) {
      Object.values(this.saleOrderLineForm.controls).forEach(control => {
        if (control.invalid) {
          control.markAsDirty();
          control.updateValueAndValidity({ onlySelf: true });
        }
      });
      return;
    }

    const saleOrderId = this.nzData.saleOrder?.id || this.nzData.saleOrderId;
    const formValue = this.saleOrderLineForm.getRawValue();

    // Helper para convertir IDs opcionales: si es 0 o no existe, retorna null
    const getIdValue = (value: any): number | null => {
      return value && value !== 0 ? value : null;
    };

    // Determinar taxIds: convertir de single a array explícito (nunca null/omitido).
    const taxIds = resolveExplicitTaxIds(formValue.taxIds);

    // Construir el payload según el formato del endpoint SaleOrderLine
    // No incluir campos con valor null o 0 para evitar errores del backend
    const payload: any = {
      saleOrderId: saleOrderId ? (typeof saleOrderId === 'string' ? parseInt(saleOrderId) : saleOrderId) : null,
      name: formValue.name || '',
      customerNote: this.nzData.lineData?.customerNote || null,
      quantity: formValue.quantity || 0,
      priceUnit: formValue.price || 0,
      discount: formValue.discount || 0,
      sequence: 0, // Se calculará en el servidor
      isActive: true
    };

    // Solo agregar productId si tiene un valor válido (no null y no 0)
    const productId = getIdValue(formValue.productId);
    if (productId && productId !== 0) {
      payload.productId = productId;
    }

    const supplierId = getIdValue(formValue.supplierId);

    // Solo agregar unitOfMeasureId si tiene un valor válido (no null y no 0)
    const unitOfMeasureId = getIdValue(formValue.unitOfMeasureId);
    if (unitOfMeasureId && unitOfMeasureId !== 0) {
      payload.unitOfMeasureId = unitOfMeasureId;
    }

    // Solo agregar currencyId si tiene un valor válido (no null y no 0)
    const currencyId = getIdValue(formValue.currencyId) || this.getSaleOrderCurrencyId();
    if (currencyId && currencyId !== 0) {
      payload.currencyId = currencyId;
    }

    // Enviar taxIds siempre explícito: el backend distingue ausente (no tocar) de [] (limpiar).
    payload.taxIds = taxIds;

    // Siempre incluir origen y destino (incluso si son null) especialmente para PUTs
    const sourceIdValue = getIdValue(formValue.sourceId);
    const destinationIdValue = getIdValue(formValue.destinationId);

    // Si estamos editando (PUT), siempre incluir estos campos
    if (this.isEditMode && this.nzData.lineData?.id) {
      payload.sourceId = sourceIdValue;
      payload.destinationId = destinationIdValue;
    } else {
      // Para CREATE, solo incluir si tienen valores válidos
      if (sourceIdValue && sourceIdValue !== 0) {
        payload.sourceId = sourceIdValue;
      }
      if (destinationIdValue && destinationIdValue !== 0) {
        payload.destinationId = destinationIdValue;
      }
    }

    // Si estamos editando, añadir ID
    if (this.isEditMode && this.nzData.lineData?.id) {
      payload.id = this.nzData.lineData.id;
    }

    // Si no hay ID de pedido y no estamos editando (es una línea nueva para un presupuesto nuevo)
    // permitir guardar localmente sin llamar al backend
    if (!saleOrderId && !this.isEditMode) {
      const lineData = { ...payload, supplierId };
      if (createNew) {
        this.lineCreated.emit(lineData);
        this.saleOrderLineForm.reset({
          name: '',
          productId: null,
          supplierId: 0,
          quantity: 0,
          unitOfMeasureId: 0,
          price: 0,
          discount: 0,
          currencyId: this.nzData.saleOrder?.currencyId || 1,
          amount: 0,
          taxIds: null
        });
        this.filteredUnitList = this.unitList;
        this.saleOrderLineForm.get('unitOfMeasureId')?.enable();
        this.calculateAmount();
        this.updateFilteredTaxes();
      } else {
        this.drawerRef.close(lineData);
      }
      return;
    }

    if (!saleOrderId) {
      console.error('No se puede guardar: falta ID de pedido');
      return;
    }

    this.saving = true;

    const request$ = this.isEditMode && this.nzData.lineData?.id
      ? this.opportunityService.updateSaleOrderLine(payload.id, payload)
      : this.opportunityService.createSaleOrderLine(payload);

    request$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        map((response: any) => {
          const resultData = response?.data || response;
          const rawLineData = Array.isArray(resultData) ? resultData[0] : resultData;
          const lineData = rawLineData?.data || rawLineData;
          const savedLineData = this.mergeSavedLineWithPayload(lineData, payload);

          return { saleOrderLine: savedLineData };
        }),
        finalize(() => {
          this.saving = false;
        })
      )
      .subscribe({
        next: (result: any) => {
          const response = result?.saleOrderLine;
          // La respuesta puede devolver un array de líneas o un objeto simple
          const resultData = response?.data || response;
          const rawLineData = Array.isArray(resultData) ? resultData[0] : resultData;
          const lineData = rawLineData?.data || rawLineData;
          const savedLineData = this.mergeSavedLineWithPayload(lineData, payload);

          if (createNew) {
            // Si hay datos, emitir el evento para que el componente padre agregue la línea
            if (lineData) {
              this.lineCreated.emit(result);
            }

            // Resetear formulario para nueva línea
            this.saleOrderLineForm.reset({
              name: '',
              productId: null,
              supplierId: 0,
              quantity: 0,
              unitOfMeasureId: 0,
              price: 0,
              discount: 0,
              currencyId: this.nzData.saleOrder?.currencyId || 1,
              amount: 0,
              taxIds: null
            });
            this.filteredUnitList = this.unitList;
            this.saleOrderLineForm.get('unitOfMeasureId')?.enable();
            this.calculateAmount();
            this.updateFilteredTaxes();
          } else {
            // Cerrar drawer y devolver los datos guardados preservando los campos enviados.
            this.drawerRef.close(this.isEditMode ? savedLineData : result);
          }
        },
        error: (error) => this.handleSaveError(error)
      });
  }

  /**
   * CEC-687. Enseña por qué el servidor rechazó la línea. Antes solo iba a la consola: el comercial
   * pulsaba Guardar y no pasaba nada. El drawer se queda abierto para no perder lo escrito; como solo
   * se cierra al guardar bien, la rejilla no ha pintado nada que haya que deshacer.
   */
  private handleSaveError(error: unknown): void {
    console.error(this.isEditMode ? 'Error updating sale order line:' : 'Error creating sale order line:', error);

    this.showToast(
      this.isEditMode ? 'No se pudo guardar el cambio en la línea' : 'No se pudo crear la línea',
      'error',
      resolveLineWriteErrorMessage(error, 'Inténtalo de nuevo o avisa a soporte.'),
    );
  }

  private mergeSavedLineWithPayload(lineData: any, payload: any): any {
    const mergedLineData = {
      ...payload,
      ...(lineData || {})
    };

    if (Object.prototype.hasOwnProperty.call(payload, 'supplierId')) {
      mergedLineData.supplierId = payload.supplierId;

      // If the provider was cleared, do not let a stale supplier object from the response restore it.
      if (payload.supplierId === null) {
        mergedLineData.supplier = null;
      } else if (mergedLineData.supplier) {
        const responseSupplierId = typeof mergedLineData.supplier === 'object'
          ? mergedLineData.supplier.id
          : mergedLineData.supplier;

        if (Number(responseSupplierId) !== Number(payload.supplierId)) {
          mergedLineData.supplier = null;
        }
      }
    }

    if (payload.taxIds && (!mergedLineData.taxIds || mergedLineData.taxIds.length === 0)) {
      mergedLineData.taxIds = payload.taxIds;
    }

    return mergedLineData;
  }
}

